import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, type AdminCustomerRow } from '@/services/adminApi';
import { useAdminExportCsv } from '@/hooks/admin/useAdminExportCsv';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';
import { SortableTableHeader, type SortDir } from '@/components/admin/SortableTableHeader';
import { CompanyQuickActions } from '@/components/admin/CompanyQuickActions';
import { BulkActionsBar } from '@/components/admin/BulkActionsBar';
import { BulkDeleteDialog } from '@/components/admin/BulkDeleteDialog';

/**
 * AdminCustomersPage — list of customers cross-tenant with rich
 * filtering (segment, plan, isActive, trial status, created date
 * range), in-memory text search across loaded rows, manual refresh,
 * cursor pagination, and CSV export.
 */
export default function AdminCustomersPage() {
  // Server filters
  const [segment, setSegment] = useState<string>('all');
  const [plan, setPlan] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all'); // 'all'|'true'|'false'
  const [trialStatus, setTrialStatus] = useState<string>('all');
  const [createdFrom, setCreatedFrom] = useState<string>('');
  const [createdTo, setCreatedTo] = useState<string>('');
  // Client-only inline search across loaded rows
  const [searchInline, setSearchInline] = useState('');

  // Sprint admin_sort_all_tables (30/05/26)
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSortChange = (field: string | null, dir: SortDir) => {
    setSortBy(field);
    setSortDir(dir);
  };

  const filterParams = useMemo(() => ({
    ...(segment !== 'all' ? { segment } : {}),
    ...(plan !== 'all' ? { plan } : {}),
    ...(activeFilter !== 'all' ? { isActive: activeFilter === 'true' } : {}),
    ...(trialStatus !== 'all' ? { trialStatus } : {}),
    ...(createdFrom ? { createdFrom } : {}),
    ...(createdTo ? { createdTo } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
  }), [segment, plan, activeFilter, trialStatus, createdFrom, createdTo, sortBy, sortDir]);

  const { exportCsv, isExporting } = useAdminExportCsv('customers', filterParams);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.customers.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<AdminCustomerRow>(fetchPage, [filterParams]);

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      (r.publicName || r.name).toLowerCase().includes(s)
      || (r.ownerEmail ?? '').toLowerCase().includes(s)
      || (r.ownerName ?? '').toLowerCase().includes(s)
      || r.id.toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  // Bulk select state (Admin SUPER-MVP M3, 27/05/26): selectedIds Set<companyId>
  // della selezione attiva. Si svuota dopo un bulk delete success oppure
  // explicit clear. Persiste tra refresh ma viene filtrato per ids esistenti.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  const allFilteredIds = useMemo(() => new Set(filtered.map((r) => r.id)), [filtered]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const someFilteredSelected = filtered.some((r) => selectedIds.has(r.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const id of allFilteredIds) next.delete(id);
      } else {
        for (const id of allFilteredIds) next.add(id);
      }
      return next;
    });
  };

  const toggleRowSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Active filter chips
  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (segment !== 'all') activeFilters.push({ key: 'segment', label: `Segment: ${segment}`, onClear: () => setSegment('all') });
  if (plan !== 'all') activeFilters.push({ key: 'plan', label: `Plan: ${plan}`, onClear: () => setPlan('all') });
  if (activeFilter !== 'all') activeFilters.push({ key: 'active', label: activeFilter === 'true' ? 'Active' : 'Inactive', onClear: () => setActiveFilter('all') });
  if (trialStatus !== 'all') activeFilters.push({ key: 'trial', label: `Trial: ${trialStatus}`, onClear: () => setTrialStatus('all') });
  if (createdFrom) activeFilters.push({ key: 'from', label: `From: ${createdFrom}`, onClear: () => setCreatedFrom('') });
  if (createdTo) activeFilters.push({ key: 'to', label: `To: ${createdTo}`, onClear: () => setCreatedTo('') });
  if (searchInline) activeFilters.push({ key: 'search', label: `Search: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setSegment('all'); setPlan('all'); setActiveFilter('all'); setTrialStatus('all');
    setCreatedFrom(''); setCreatedTo(''); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Aziende"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        onExportCsv={exportCsv}
        isExporting={isExporting}
      />

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Cerca per nome, email, ID..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[260px]"
        />
        <Select value={segment} onValueChange={setSegment}>
          <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Segmento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i segmenti</SelectItem>
            <SelectItem value="sme">SME</SelectItem>
            <SelectItem value="agency">Agency</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Piano" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i piani</SelectItem>
            <SelectItem value="freemium_trial">Freemium trial</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="growth">Growth</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="agency_starter">Agency Starter</SelectItem>
            <SelectItem value="agency_medium">Agency Medium</SelectItem>
            <SelectItem value="agency_professional">Agency Pro</SelectItem>
            <SelectItem value="unlimited">Unlimited</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger className="h-9 w-[120px]"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualsiasi stato</SelectItem>
            <SelectItem value="true">Attiva</SelectItem>
            <SelectItem value="false">Sospesa</SelectItem>
          </SelectContent>
        </Select>
        <Select value={trialStatus} onValueChange={setTrialStatus}>
          <SelectTrigger className="h-9 w-[150px]"><SelectValue placeholder="Trial" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Qualsiasi trial</SelectItem>
            <SelectItem value="active">Trial attivo</SelectItem>
            <SelectItem value="converted">Convertito</SelectItem>
            <SelectItem value="expired">Scaduto</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Creata</span>
          <Input
            type="date"
            value={createdFrom}
            onChange={(e) => setCreatedFrom(e.target.value)}
            className="h-9 w-[150px]"
          />
          <span className="text-xs text-muted-foreground">→</span>
          <Input
            type="date"
            value={createdTo}
            onChange={(e) => setCreatedTo(e.target.value)}
            className="h-9 w-[150px]"
          />
        </div>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">Error: {error}</p>}

      <BulkActionsBar
        count={selectedIds.size}
        entityLabel="aziende"
        onClearSelection={() => setSelectedIds(new Set())}
        onBulkDelete={() => setBulkDialogOpen(true)}
      />

      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-3 px-4 w-[40px]">
                    <Checkbox
                      checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Seleziona tutte"
                    />
                  </th>
                  <th className="text-left py-3 px-4">
                    <SortableTableHeader label="Azienda" field="name" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Owner</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Segmento" field="segment" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Piano</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Trial</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Stato</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Creata" field="createdAt" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} />
                  </th>
                  <th className="text-right font-medium text-muted-foreground py-3 px-3 w-[60px]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="py-12 text-center text-muted-foreground">Caricamento...</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={9} className="py-12 text-center text-muted-foreground">Nessuna azienda corrisponde ai filtri attuali.</td></tr>
                )}
                {filtered.map((row) => (
                  <CustomerRow
                    key={row.id}
                    row={row}
                    selected={selectedIds.has(row.id)}
                    onToggleSelect={() => toggleRowSelect(row.id)}
                    onUpdated={refresh}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <AdminListFooter
            loadedCount={items.length}
            filteredCount={searchInline ? filtered.length : undefined}
            totalCount={totalCount}
            entityLabel="azienda"
            entityLabelPlural="aziende"
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </CardContent>
      </Card>
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}

      <BulkDeleteDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        entityLabel="aziende"
        count={selectedIds.size}
        ids={Array.from(selectedIds)}
        onConfirm={async (reason) => {
          const result = await adminApi.customers.bulkDelete(Array.from(selectedIds), reason);
          setSelectedIds(new Set());
          refresh();
          return { deleted: result.summary.deleted, failed: result.summary.failed };
        }}
      />
    </div>
  );
}

function CustomerRow({
  row,
  selected,
  onToggleSelect,
  onUpdated,
}: {
  row: AdminCustomerRow;
  selected: boolean;
  onToggleSelect: () => void;
  onUpdated: () => void;
}) {
  return (
    <tr className={`border-b border-border/30 last:border-0 ${selected ? 'bg-primary/5' : 'hover:bg-accent/40'}`}>
      <td className="py-3 px-4">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          aria-label={`Seleziona ${row.publicName || row.name}`}
        />
      </td>
      <td className="py-3 px-4">
        <Link to={`/admin/customers/${row.id}`} className="font-medium hover:underline">
          {row.publicName || row.name}
        </Link>
        <p className="text-xs text-muted-foreground truncate max-w-[260px]" title={row.id}>
          <code className="text-[10px]">{row.id}</code>
        </p>
      </td>
      <td className="py-3 px-3">
        {row.ownerUid ? (
          <Link
            to={`/admin/users/${row.ownerUid}`}
            className="text-xs hover:underline"
            title={row.ownerEmail ?? row.ownerUid}
          >
            {row.ownerName || row.ownerEmail || '—'}
            {row.ownerEmail && row.ownerName && (
              <span className="block text-[10px] text-muted-foreground truncate max-w-[200px]">
                {row.ownerEmail}
              </span>
            )}
          </Link>
        ) : row.ownerEmail ? (
          <span className="text-xs text-muted-foreground">{row.ownerEmail}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="py-3 px-3">
        <span className="text-xs px-2 py-0.5 rounded-md bg-muted capitalize">{row.segment}</span>
      </td>
      <td className="py-3 px-3 text-muted-foreground">{row.planId ?? '—'}</td>
      <td className="py-3 px-3 text-muted-foreground">
        {row.trialStatus ? (
          <TrialLabel status={row.trialStatus} endsAt={row.trialEndsAt} />
        ) : '—'}
      </td>
      <td className="py-3 px-3">
        {row.isActive ? (
          <span className="text-xs text-success">attiva</span>
        ) : (
          <span className="text-xs text-destructive">sospesa</span>
        )}
      </td>
      <td className="py-3 px-3 text-muted-foreground text-xs">
        {row.createdAt ? new Date(row.createdAt).toLocaleDateString('it-IT') : '—'}
      </td>
      <td className="py-2 px-2 text-right" onClick={(e) => e.stopPropagation()}>
        <CompanyQuickActions row={row} onUpdated={onUpdated} />
      </td>
    </tr>
  );
}

function TrialLabel({ status, endsAt }: { status: string; endsAt?: string }): JSX.Element {
  const date = endsAt ? new Date(endsAt).toLocaleDateString('en-GB') : null;
  if (status === 'active') {
    return <span className="text-xs">{date ? `expires ${date}` : 'active'}</span>;
  }
  if (status === 'expired') {
    return <span className="text-xs">{date ? `expired on ${date}` : 'expired'}</span>;
  }
  if (status === 'converted') {
    return <span className="text-xs">{date ? `converted on ${date}` : 'converted'}</span>;
  }
  return <span className="text-xs">{status}{date ? ` · ${date}` : ''}</span>;
}
